import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { AdminOrderServices } from "../../services/admin/AdminOrderServices";
import { Filter, ArrowRight, Loader2, PackageOpen } from "lucide-react";
import { formatRelativeTime } from "../../utils/dateUtils";

export default function OrdersPage() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("");

    // Pagination
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const fetchOrders = async (currentPage = 1, filter = "") => {
        try {
            setLoading(true);
            const res = await AdminOrderServices.getOrders(currentPage, 50, filter);
            if (res.status === "success") {
                setOrders(res.data.orders);
                setTotalPages(res.data.pages);
                setPage(res.data.current_page);
            }
        } catch (error) {
            console.error("Failed to fetch orders:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders(1, statusFilter);
    }, [statusFilter]);

    const handleNextPage = () => {
        if (page < totalPages) {
            fetchOrders(page + 1, statusFilter);
        }
    };

    const handlePrevPage = () => {
        if (page > 1) {
            fetchOrders(page - 1, statusFilter);
        }
    };

    const getStatusBadge = (status) => {
        switch (status.toLowerCase()) {
            case "paid":
            case "completed":
                return <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">Paid</span>;
            case "shipped":
                return <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">Shipped</span>;
            case "processing":
            case "pending":
                return <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">Processing</span>;
            default:
                return <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">{status}</span>;
        }
    };

    return (
        <div className="p-6 lg:p-10 max-w-7xl mx-auto min-h-screen">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <h1 className="text-3xl font-normal text-black dark:text-white mb-2" style={{ fontFamily: "Playfair Display, serif" }}>Purchases & Orders</h1>
                    <p className="text-sm text-black dark:text-white/50 mt-1">Manage customer orders and fulfill merchandise.</p>
                </div>

                <div className="flex gap-4 w-full md:w-auto">
                    <div className="relative flex-1 md:w-64">
                        <Filter className="absolute left-4 top-1/2 -translate-y-1/2 text-black dark:text-white/40" size={16} />
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="w-full pl-12 pr-4 py-3 rounded-xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#1A1A1A] text-sm font-medium focus:border-jazz-brown outline-none appearance-none"
                        >
                            <option value="">All Statuses</option>
                            <option value="paid">Paid (Processing)</option>
                            <option value="shipped">Shipped</option>
                            <option value="completed">Completed</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className="bg-white dark:bg-[#1A1A1A] rounded-2xl border border-black/5 dark:border-white/5 shadow-sm overflow-hidden flex flex-col min-h-[500px]">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-b border-black/5 dark:border-white/5 bg-[#FAFAFA] dark:bg-[#222]">
                                <th className="px-6 py-4 text-xs font-bold uppercase tracking-widest text-black dark:text-white/50">Order Ref</th>
                                <th className="px-6 py-4 text-xs font-bold uppercase tracking-widest text-black dark:text-white/50">Customer</th>
                                <th className="px-6 py-4 text-xs font-bold uppercase tracking-widest text-black dark:text-white/50">Date</th>
                                <th className="px-6 py-4 text-xs font-bold uppercase tracking-widest text-black dark:text-white/50 text-right">Amount</th>
                                <th className="px-6 py-4 text-xs font-bold uppercase tracking-widest text-black dark:text-white/50 text-center">Status</th>
                                <th className="px-6 py-4 text-xs font-bold uppercase tracking-widest text-black dark:text-white/50 text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-black/5 dark:divide-white/5">
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-12 text-center">
                                        <Loader2 size={32} className="mx-auto animate-spin text-jazz-brown mb-4" />
                                        <p className="text-sm font-bold tracking-widest uppercase text-black dark:text-white/50">Loading Orders...</p>
                                    </td>
                                </tr>
                            ) : orders.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-16 text-center">
                                        <div className="w-16 h-16 bg-black/5 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <PackageOpen size={24} className="text-black/40 dark:text-white/30" />
                                        </div>
                                        <p className="text-sm font-bold tracking-widest uppercase text-black dark:text-white/50 mb-2">No Orders Found</p>
                                        <p className="text-xs text-black dark:text-white/40 italic">There are no orders matching your criteria.</p>
                                    </td>
                                </tr>
                            ) : (
                                orders.map(order => (
                                    <tr key={order.id} className="hover:bg-[#FAFAFA] dark:hover:bg-[#222] transition-colors group">
                                        <td className="px-6 py-4">
                                            <p className="text-sm font-bold font-mono text-black dark:text-white">{order.reference}</p>
                                        </td>
                                        <td className="px-6 py-4">
                                            <p className="text-sm font-medium text-black dark:text-white">{order.customer.full_name}</p>
                                            <p className="text-xs text-black dark:text-white/50">{order.customer.email}</p>
                                        </td>
                                        <td className="px-6 py-4">
                                            <p className="text-sm text-black dark:text-white">{new Date(order.created_at).toLocaleDateString()}</p>
                                            <p className="text-xs text-black dark:text-white/50">{formatRelativeTime(order.created_at)}</p>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <p className="text-sm font-bold font-serif text-black dark:text-white">${order.amount.toFixed(2)}</p>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            {getStatusBadge(order.status)}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <Link
                                                to={`/admin/orders/${order.id}`}
                                                className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-black/5 hover:bg-jazz-brown hover:text-white text-black transition-colors"
                                            >
                                                <ArrowRight size={18} />
                                            </Link>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="mt-auto p-4 border-t border-black/5 flex items-center justify-between text-sm">
                        <span className="text-black dark:text-white/60 font-medium">Page {page} of {totalPages}</span>
                        <div className="flex gap-2">
                            <button
                                onClick={handlePrevPage}
                                disabled={page === 1}
                                className="px-4 py-2 border border-black/10 dark:border-white/10 rounded-lg hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition disabled:opacity-50 disabled:cursor-not-allowed font-bold uppercase tracking-wider text-xs text-black dark:text-white"
                            >
                                Prev
                            </button>
                            <button
                                onClick={handleNextPage}
                                disabled={page === totalPages}
                                className="px-4 py-2 border border-black/10 dark:border-white/10 rounded-lg hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition disabled:opacity-50 disabled:cursor-not-allowed font-bold uppercase tracking-wider text-xs text-black dark:text-white"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
